import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  CLINICAL_STAGE_LABEL,
  formatBloodPressurePulse,
  SUBSCRIPTION_STANDING_LABEL,
  type PatientDetail,
} from "@care-by-ella/contracts";
import { PatientGroupBadge } from "./PatientGroupBadge";
import { GoalWeightCorrection } from "./GoalWeightCorrection";
import { GoalZoneNote } from "./GoalZoneNote";
import { journeyStateLabel, treatmentStateLabel } from "@/lib/state-labels";
import { acuteAlarmHeading, headerRiskCounts } from "@/lib/attention-texts";
import { feedbackConsentLine } from "@/lib/feedback-consent";
import {
  SignOutPatientEverywhere,
  SupportAccessUntil,
  useViewPath,
} from "./SupportView";

// TES-520 — the person, always.
//
// "It needs to always make the doctor/coach aware of the
// person they look at and the most important vitals." The old header was
// five lines of metadata in five different sizes, and the numbers sat in a
// card UNDER the tab bar, so they vanished the moment a tab changed. This is
// one white card, pinned above the tabs on every tab: who she is, then the
// numbers a clinician glances at before anything else.
//
// Brand sheet 2b, followed to the letter: a medical card is white with black
// values and plum as the accent; emphasis is weight, never colour or italics.
// So the vitals are Playfair numbers in ink over sentence-case labels, with
// hairlines between them — one strip, not four tiles. Red is reserved for
// out-of-range and alerts; weight gain is never red.

type Intake = PatientDetail["formSubmissions"][number] | undefined;

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
  });
}

function initials(name: string | null, fallback: string): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || fallback;
}

/** Height from the intake answers, if the template captured it. */
function heightCm(intake: Intake): number | null {
  // TES-558: `med_height` since intake v2 (v8 in production); `heightCm` is v1.
  const raw = intake?.answers?.["med_height"] ?? intake?.answers?.["heightCm"];
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function Vital({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: "danger" | "warning" | "brand";
}) {
  const valueClass =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : "text-textPrimary";
  return (
    <div className="flex min-w-0 flex-col gap-[2px] lg:px-md lg:first:pl-0 lg:last:pr-0">
      <span className="text-xs text-textSecondary">{label}</span>
      <span
        className={`truncate font-display text-[22px] leading-7 ${valueClass}`}
      >
        {value}
      </span>
      {detail ? (
        <span
          className={`truncate text-xs ${tone === "brand" ? "font-semibold text-brand" : "text-textSecondary"}`}
        >
          {detail}
        </span>
      ) : null}
    </div>
  );
}

export function PatientHeader({
  data,
  intake,
  lastCheckinAt,
  actions,
  onGoalWeightAdjusted,
  onUnauthorized,
  nameHref,
}: {
  data: PatientDetail;
  intake: Intake;
  lastCheckinAt: string | null;
  /** The screen's primary actions, rendered top-right. */
  actions?: ReactNode;
  // TES-863 (Ö-229) — omitted by the read-only surfaces that reuse this
  // header. No handler, no correction control: a doctor should not be shown
  // an editor on a screen whose job is to show him what is.
  onGoalWeightAdjusted?: () => void;
  onUnauthorized?: () => void;
  // TES-955 (TES-941) — "Patientens namn ska alltid vara klickbart, även från mötes- och granskningslägen."
  // The name leads to the patient's overview; a caller that keeps state in
  // the URL (the open meeting) passes its own spelling of that link.
  nameHref?: string;
}) {
  const viewPath = useViewPath();
  const last6 = data.patient.personalNumber.slice(-6);
  const name = data.patient.name ?? `Patient …${last6}`;
  const gp = data.goalProgress;
  const h = heightCm(intake);
  const bmi =
    gp?.currentKg != null && h
      ? Math.round((gp.currentKg / Math.pow(h / 100, 2)) * 10) / 10
      : null;

  // TES-944 — "Riskflaggor: X" counts flags and hard stops from EVERY
  // submission, not only the intake (TES-941: "oavsett var de fångats upp").
  // TES-1001 (Ö-250) — the open acute alarms are counted apart, as
  // "Akutlarm: X – agera nu", and never also in "Riskflaggor: X". The server
  // splits them with the same code as the overview's list; headerRiskCounts
  // keeps an older server's combined number from counting an alarm twice.
  const { acute: alerts, flags: formFlags } = headerRiskCounts({
    acuteAlarmCount: data.acuteAlarmCount,
    formFlagCount: data.formFlagCount,
    openAlarms: data.riskFlags?.length ?? 0,
    fallbackFlagSum: data.formSubmissions.reduce(
      (n, s) => n + s.flags.length + s.hardStops.length,
      0,
    ),
  });

  // TES-944 (TES-941 §PATIENTHEADER) — clinical phase and subscription are two
  // fields. "Nästa möte" is gone as a key field: meetings belong to the start,
  // and follow-up afterwards runs through the monthly check-ins. An older API
  // without the derived phase falls back to the previous single status.
  const clinicalStage = data.clinicalStage
    ? CLINICAL_STAGE_LABEL[data.clinicalStage]
    : data.treatment
      ? treatmentStateLabel(data.treatment.state)
      : journeyStateLabel(data.patient.journeyState, {
          transferApproved: data.transferReview?.status === "approved",
        });
  const subscription = data.subscription
    ? SUBSCRIPTION_STANDING_LABEL[data.subscription.standing]
    : null;
  // Nothing at all when there is no reading — no "saknas", no warning.
  const bloodPressurePulse = formatBloodPressurePulse(data.bloodPressurePulse);

  // Δ since start is shown with weight only — never colour-coded (2b: red is
  // banned for weight gain). Plum marks progress; gain is just a number.
  const lost = gp?.lostKg ?? null;
  const weightDetail =
    gp?.baselineKg != null
      ? lost != null && lost > 0
        ? `−${lost} kg sedan start`
        : `start ${gp.baselineKg} kg`
      : undefined;

  const goalDetail =
    gp?.goalWeightKg != null && gp?.currentKg != null
      ? gp.reachedGoal
        ? "målet nått"
        : `${Math.round((gp.currentKg - gp.goalWeightKg) * 10) / 10} kg kvar`
      : undefined;

  // Emelie's label, exactly: "Riskflaggor: X". No qualifier such as
  // "unresolved", since neither resolving nor following up is defined yet.
  const riskTone = formFlags > 0 ? ("warning" as const) : undefined;

  return (
    <div className="rounded-lg border border-surfaceMuted bg-surface px-md pt-md pb-md shadow-[0_1px_0_0_rgba(97,32,95,0.06)] lg:px-lg lg:pt-lg">
      {/* ---- who ---- */}
      <div className="flex flex-wrap items-start justify-between gap-md">
        <div className="flex min-w-0 items-center gap-md">
          <span
            aria-hidden="true"
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand/10 font-display text-xl text-brand"
          >
            {initials(data.patient.name, last6.slice(0, 2))}
          </span>
          <div className="min-w-0">
            <h1 className="break-words font-display text-[28px] leading-8 tracking-tight text-textPrimary lg:truncate">
              <Link
                to={nameHref ?? viewPath(`/patients/${data.patient.id}`)}
                className="hover:text-brand hover:underline"
                title="Till patientens översikt"
              >
                {name}
              </Link>
            </h1>
            <div className="mt-xs flex flex-wrap items-center gap-sm text-sm text-textSecondary">
              <span className="font-mono text-xs">…{last6}</span>
              <PatientGroupBadge group={data.patientGroup} />
              {data.patient.isTest ? (
                <span className="rounded border border-warning/40 bg-warning/15 px-xs py-[2px] text-xs font-medium uppercase tracking-wide text-warning">
                  Test
                </span>
              ) : null}
              <span>
                {data.patient.preferredLocale === "en" ? "Engelska" : "Svenska"}
              </span>
            </div>
          </div>
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-sm">
            {actions}
          </div>
        ) : null}
      </div>

      {/* ---- the numbers ---- */}
      {/* TES-966 — a grid on a phone: in one row the eight figures were
          squeezed to a letter each. From `lg` up, the divided row as before. */}
      <div className="mt-md grid grid-cols-2 gap-md overflow-x-auto border-t border-surfaceMuted pt-md sm:grid-cols-3 lg:flex lg:gap-0 lg:divide-x lg:divide-surfaceMuted">
        <Vital
          label="Vikt"
          value={gp?.currentKg != null ? `${gp.currentKg} kg` : "—"}
          detail={weightDetail}
          tone={lost != null && lost > 0 ? "brand" : undefined}
        />
        <div>
          <Vital
            label="Målvikt"
            value={gp?.goalWeightKg != null ? `${gp.goalWeightKg} kg` : "—"}
            detail={goalDetail}
            tone={gp?.reachedGoal ? "brand" : undefined}
          />
          {/* TES-998 — the goal-zone state and her F-02 answer. */}
          <GoalZoneNote summary={data.goalZone} />
          {onGoalWeightAdjusted ? (
            <GoalWeightCorrection
              patientId={data.patient.id}
              current={gp?.goalWeightKg ?? null}
              onSaved={onGoalWeightAdjusted}
              onUnauthorized={onUnauthorized}
            />
          ) : null}
        </div>
        <Vital
          label="BMI"
          value={bmi != null ? String(bmi) : "—"}
          detail={h ? `${h} cm` : "längd saknas"}
        />
        {bloodPressurePulse ? (
          <Vital label="Blodtryck och puls" value={bloodPressurePulse} />
        ) : null}
        <Vital label="Klinisk fas" value={clinicalStage} />
        {subscription ? (
          <Vital label="Abonnemang" value={subscription} />
        ) : null}
        {/* TES-1001 (Ö-250) — acute alarms first, on their own. */}
        {alerts > 0 ? (
          <Vital
            label="Akutlarm"
            value={acuteAlarmHeading(alerts)}
            tone="danger"
          />
        ) : null}
        <Vital
          label="Alla formulär"
          value={`Riskflaggor: ${formFlags}`}
          tone={riskTone}
        />
        <Vital
          label="Senaste incheckning"
          value={lastCheckinAt ? fmtDay(lastCheckinAt) : "—"}
          detail={lastCheckinAt ? undefined : "ingen än"}
        />
      </div>

      {/* ---- the paperwork, out of the way ---- */}
      <p className="mt-md flex flex-wrap gap-x-md gap-y-xs text-xs text-textSecondary">
        <span>
          {data.f01
            ? `F-01 ${fmtDay(data.f01.submittedAt)}${data.f01.source === "lead" ? " via webben" : ""}`
            : "F-01 ej besvarad"}
        </span>
        {/* TES-504 — the mandatory contact step: the doctor sees whether the
            patient can be reached by mail, never the address itself here. */}
        <span>
          {data.patient.emailStatus === "verified"
            ? "E-post bekräftad"
            : data.patient.emailStatus === "unverified"
              ? "E-post obekräftad"
              : "E-post saknas"}
        </span>
        {/* TES-1106 — may Ella contact her with product-feedback questions:
            her current choice, when she made it and which wording she saw. */}
        <span>{feedbackConsentLine(data.patient.productFeedbackConsent)}</span>
        <span>
          {data.patient.webdocPatientRef ? (
            <>
              Webdoc{" "}
              <code className="text-textPrimary">
                {data.patient.webdocPatientRef}
              </code>
            </>
          ) : (
            <span className="text-warning">Webdoc synkroniserar</span>
          )}
        </span>
        <span>Registrerad {fmtDay(data.patient.createdAt)}</span>
        {/* TES-1040 — the admin's support grant for this patient. */}
        <SupportAccessUntil patientId={data.patient.id} />
        {/* TES-1205 — support ends her app sessions (lost phone). */}
        <SignOutPatientEverywhere patientId={data.patient.id} />
      </p>
    </div>
  );
}
